/**
 * @module cercaliagl/MapStyler
 */
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import Default from './mapstyles/default.json';
import Empty from './mapstyles/empty.json';
import MapEventType from './MapEventType';
import Satellite from './mapstyles/satellite.json';
import GoogleStyle from './mapstyles/cercalia_google_style.json';
import GoogleStyleV2 from './mapstyles/cercalia_google_style_v2.json';
import GoogleStyleTransparent from './mapstyles/google_style_semitransparencia.json';
import OSM from './mapstyles/osm.json';
import Dark from './mapstyles/dark.json';
import Silver from './mapstyles/silver.json';
import SilverMut from './mapstyles/silver_mut.json';
import { cloneDeep, geofencing } from './util';
import { CANARY_ISLANDS_BOUNDS, CDN_SUBDOMAINS, KEY, LB_DOMAIN, SPAIN_BOUNDS, } from './globals';
import MapTypes from './MapTypes';
import LonLat from './LonLat.js';
/**
 * @typedef {Object} MapboxStyleJSON
 * @property {string} attribution Attribution text
 * @property {number} version Version
 * @property {string} name Style name
 * @property {string} sprite Sprite icons
 * @property {string} glyphs Glyphs
 * @property {Object.<string, StyleSourceJSON>} sources Source
 * @property {Array.<StyleLayerJSON>} layers Layers
 */
/**
 * @typedef {Object} StyleSourceJSON
 * @property {string} type Type
 * @property {string} url Url
 */
/**
 * @typedef {Object} StyleLayerJSON
 * @property {string} id Id
 * @property {string} type Type
 * @property {string} source Source id
 * @property {string} source-layer Source layer
 * @property {string} minzoom Min zoom
 * @property {string} maxzoom Max zoom
 * @property {Object} filter Filter
 * @property {Object.<string, any>} layout Layout
 * @property {Object.<string, any>} paint Layout
 */
/**
 * @typedef {Object} LanguagesSourceLayer
 * @property {string} sourceLayer
 * @property {LanguageSourceLayerValue} value
 */
/**
 * @typedef {Object} LanguageSourceLayerValue
 * @property {string} original Original 'text-font' value for layer.
 * @property {Array.<string>} languages Available languages
 */
/**
 * @enum {string}
 */
export var BaseLayerType = {
    VECTOR_TILES: 'vectortiles',
    RASTER: 'raster',
};
/**
 * @enum {string}
 */
export var ReferenceLayer = {
    BEFORE_LABELS: 'poi-level-4',
};
function addAuthkey(styles) {
    var openmaptiles = styles.sources.openmaptiles;
    if (openmaptiles && openmaptiles.tiles) {
        openmaptiles.tiles = openmaptiles.tiles.map(function (tile) { return tile.replace('{key}', KEY); });
    }
}
function replaceCDNSubdomains(styles) {
    var sources = styles.sources;
    if (sources) {
        var sourcesKeys = Object.keys(sources);
        sourcesKeys.forEach(function (sourceKey) {
            var currentTiles = sources[sourceKey].tiles;
            if (currentTiles) {
                var modelUrl_1 = currentTiles[0];
                if (modelUrl_1.indexOf('%REPLACE_CDN%') !== -1) {
                    sources[sourceKey].tiles = CDN_SUBDOMAINS.map(function (subdomain) { return modelUrl_1.replace('%REPLACE_CDN%', subdomain); });
                }
                else if (modelUrl_1.indexOf('%REPLACE_LB%') !== -1) {
                    sources[sourceKey].tiles = [modelUrl_1.replace('%REPLACE_LB%', LB_DOMAIN)];
                }
            }
            var currentUrl = sources[sourceKey].url;
            if (currentUrl) {
                if (currentUrl.indexOf('%REPLACE_LB%') !== -1) {
                    sources[sourceKey].url = currentUrl.replace('%REPLACE_LB%', LB_DOMAIN);
                }
                else if (currentUrl.indexOf('%REPLACE_CDN%') !== -1) {
                    sources[sourceKey].url = currentUrl.replace('%REPLACE_CDN%', CDN_SUBDOMAINS[0]);
                }
            }
        });
    }
    if (styles.sprite && styles.sprite.indexOf('%REPLACE_LB%') !== -1) {
        // eslint-disable-next-line no-param-reassign
        styles.sprite = styles.sprite.replace('%REPLACE_LB%', LB_DOMAIN);
    }
    if (styles.glyphs && styles.glyphs.indexOf('%REPLACE_LB%') !== -1) {
        // eslint-disable-next-line no-param-reassign
        styles.glyphs = styles.glyphs.replace('%REPLACE_LB%', LB_DOMAIN);
    }
}
/**
 * @classdesc
 */
var MapStyler = /** @class */ (function () {
    /**
     * @constructor
     * @param {import("./Map").default} map Map
     * @param {Array.<import("./layer/XYZ").default|import("./layer/WMS").default>} customBaseLayers Custom base layers.
     * @param {string} [mapType] Map type
     */
    function MapStyler(map, customBaseLayers, mapType) {
        var _this = this;
        if (customBaseLayers === void 0) { customBaseLayers = []; }
        if (mapType === void 0) { mapType = MapTypes.VECTOR_TILES; }
        /**
         * @private
         * @type {import("./Map").default}
         */
        this.map_ = map;
        /**
         * @private
         * @type {string}
         */
        this.currentStyle_ = mapType;
        /**
         * @private
         * @type {MapboxStyleJSON}
         */
        this.styles_ = null;
        /**
         * @private
         * @type {string}
         */
        this.baseLayerType_ = null;
        /**
         * @private
         * @type {Object.<string, Style>}
         */
        this.customLayers_ = {};
        /**
         * @private
         * @type {Object.<string, Array.<string>>}
         */
        this.langsSourceLayers_ = null;
        customBaseLayers.forEach(function (customBaseLayer) {
            _this.addBaseLayer(customBaseLayer);
        });
        var _a = this.createStyleInner_(this.currentStyle_), style = _a.style, type = _a.type;
        this.styles_ = style;
        this.baseLayerType_ = type;
    }
    /**
     * @private
     * @param {string} id Base layer id
     * @return {Object.<Array.<any>, string>}
     */
    MapStyler.prototype.createStyleInner_ = function (id) {
        var style;
        var type;
        if (id === MapTypes.CERCALIA) {
            style = cloneDeep(Default);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.VECTOR_TILES;
        }
        else if (id === MapTypes.GOOGLE_STYLE_TRANSPARENT) {
            style = cloneDeep(GoogleStyleTransparent);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.VECTOR_TILES;
        }
        else if (id === MapTypes.GOOGLE_STYLE) {
            style = cloneDeep(GoogleStyle);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.VECTOR_TILES;
        }
        else if (id === MapTypes.GOOGLE_STYLE_V2) {
            style = cloneDeep(GoogleStyleV2);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.VECTOR_TILES;
        }
        else if (id === MapTypes.DARK) {
            style = cloneDeep(Dark);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.VECTOR_TILES;
        }
        else if (id === MapTypes.SILVER) {
            style = cloneDeep(Silver);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.VECTOR_TILES;
        }
        else if (id === MapTypes.BLANKMAP) {
            style = cloneDeep(SilverMut);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.VECTOR_TILES;
        }
        else if (id === MapTypes.SATELLITE) {
            style = cloneDeep(Satellite);
            addAuthkey(style);
            replaceCDNSubdomains(style);
            type = BaseLayerType.RASTER;
        }
        else if (id === MapTypes.OSM) {
            style = cloneDeep(OSM);
            replaceCDNSubdomains(style);
            type = BaseLayerType.RASTER;
        }
        else if (id === MapTypes.EMPTY) {
            style = cloneDeep(Empty);
            replaceCDNSubdomains(style);
            type = BaseLayerType.RASTER;
        }
        else if (this.customLayers_[id]) {
            style = this.customLayers_[id];
            type = BaseLayerType.RASTER;
        }
        return { style: style, type: type };
    };
    /**
     * @return {MapboxStyleJSON} styles
     */
    MapStyler.prototype.getStyles = function () {
        return this.styles_;
    };
    /**
     */
    MapStyler.prototype.updateStyles = function () {
        var mbMap = this.map_.getMapboxglMap();
        if (mbMap) {
            mbMap.setStyle(this.styles_, { diff: false });
        }
    };
    /**
     * @return {Object.<string, StyleSourceJSON>}
     */
    MapStyler.prototype.getSources = function () {
        return this.styles_.sources;
    };
    /**
     * @return {Array.<StyleSourceJSON>}
     */
    MapStyler.prototype.getLayers = function () {
        return this.styles_.layers;
    };
    /**
     * @param {id} Source source id
     * @return {StyleSourceJSON|null} Source or null
     */
    MapStyler.prototype.getSource = function (id) {
        var sourceIds = Object.keys(this.styles_.sources);
        for (var i = 0; i < sourceIds.length; i += 1) {
            var sourceId = sourceIds[i];
            if (id === sourceId) {
                return cloneDeep(this.styles_.sources[id]);
            }
        }
        return null;
    };
    /**
     * @param {id} Source id
     * @param {StyleSourceJSON} source Source
     */
    MapStyler.prototype.addSource = function (id, source) {
        var originalSource = this.getSource(id);
        if (!originalSource) {
            this.styles_.sources[id] = source;
            this.updateStyles();
        }
        else {
            console.warn("Source with ".concat(id, " already exists")); // eslint-disable-line no-console
        }
    };
    /**
     * @param {id} Source id
     * @param {StyleSourceJSON} source Source
     */
    MapStyler.prototype.updateSource = function (id, source) {
        var originalSource = this.getSource(id);
        if (originalSource) {
            this.styles_.sources[id] = source;
            this.updateStyles();
        }
        else {
            console.warn('Source not found'); // eslint-disable-line no-console
        }
    };
    /**
     * @param {string} id Id
     * @return {number}
     */
    MapStyler.prototype.getLayerIndexById = function (id) {
        var index = -1;
        for (var i = 0; i < this.styles_.layers.length; i += 1) {
            var layer = this.styles_.layers[i];
            if (layer.id === id) {
                index = i;
                break;
            }
        }
        return index;
    };
    /**
     * @param {string} id Id
     * @return {StyleLayerJSON|null}
     */
    MapStyler.prototype.getLayerById = function (id) {
        for (var i = 0; i < this.styles_.layers.length; i += 1) {
            var layer = this.styles_.layers[i];
            if (layer.id === id) {
                return cloneDeep(layer);
            }
        }
        return null;
    };
    /**
     * @param {string}
     */
    MapStyler.prototype.getBaseLayerType = function () {
        return this.baseLayerType_;
    };
    /**
     * @param {string} id Layer id
     * @param {StyleLayerJSON} layer Layer
     */
    MapStyler.prototype.updateLayer = function (id, layer) {
        var index = this.getLayerIndexById(id);
        if (index !== -1) {
            this.styles_.layers[index] = layer;
            this.updateStyles();
        }
        else {
            console.warn("Layer ".concat(id, " not found")); // eslint-disable-line no-console
        }
    };
    /**
     * @param {StyleLayerJSON} layer Layer
     * @param {number} [index] Index of array
     */
    MapStyler.prototype.addLayer = function (layer, index) {
        if (index !== undefined) {
            this.styles_.layers.splice(index, 0, layer);
        }
        else {
            this.styles_.layers.push(layer);
        }
        this.updateStyles();
    };
    /**
     * Remove layer
     * @param {string} id Layer id
     */
    MapStyler.prototype.removeLayer = function (id) {
        var index = this.getLayerIndexById(id);
        if (index !== -1) {
            this.styles_.layers.splice(index, 1);
            this.updateStyles();
        }
    };
    /**
     * @param {import("./layer/XYZ").default|import("./layer/WMS").default} baselayer Base laeyr
     * @param {string | undefined} mapType Map type
     */
    MapStyler.prototype.addBaseLayer = function (baselayer, mapType) {
        if (mapType === void 0) { mapType = undefined; }
        if (!mapType) {
            this.customLayers_[baselayer.getLayerId()] = baselayer.getMapboxStyle();
            return;
        }
        var layerMapboxStyle = baselayer.getMapboxStyle();
        var _a = this.mergeDefaultStyleWithCustomStyle_(layerMapboxStyle, mapType), style = _a.style, type = _a.type;
        if (!style) {
            return;
        }
        this.customLayers_[baselayer.getLayerId()] = style;
        this.baseLayerType_ = type;
    };
    /**
     * @private
     * @param {Object} layerMapboxStyle  Base laeyr
     * @param {string } mapType Map type
     * @return {Object.<Array.<any>, string>}
     */
    MapStyler.prototype.mergeDefaultStyleWithCustomStyle_ = function (layerMapboxStyle, mapType) {
        var _a = this.createStyleInner_(mapType), style = _a.style, type = _a.type;
        if (!style) {
            return { style: null, type: null };
        }
        var layersWithout3d = layerMapboxStyle.layers.filter(function (layer) { return layer.id !== 'buildings3d' && layer.id !== '3dlandmarks'; });
        style.layers = style.layers.concat(layersWithout3d);
        delete style.sources.buildings3d;
        delete style.sources['3dlandmarks'];
        style.sources = __assign(__assign({}, style.sources), layerMapboxStyle.sources);
        return { style: style, type: type };
    };
    /**
     * @param {string} id id
     * @param {maplibregl.Style} baseLayer Base layer
     * @param {string | undefined} mapType Map type
     */
    MapStyler.prototype.addBaseLayerFromJson = function (id, baseLayer, mapType) {
        if (mapType === void 0) { mapType = undefined; }
        if (!mapType) {
            this.customLayers_[id] = baseLayer;
        }
        var _a = this.mergeDefaultStyleWithCustomStyle_(baseLayer, mapType), style = _a.style, type = _a.type;
        if (!style) {
            return;
        }
        this.customLayers_[id] = style;
        this.baseLayerType_ = type;
    };
    /**
     * @param {string} baselayerId Base layer id
     */
    MapStyler.prototype.setBaseLayer = function (baselayerId) {
        var mbMap = this.map_.getMapboxglMap();
        var layerManager = this.map_.getLayerManager();
        if (layerManager) {
            layerManager.detachLayers();
            var _a = this.createStyleInner_(baselayerId), style = _a.style, type = _a.type;
            this.styles_ = style;
            this.baseLayerType_ = type;
            mbMap.once('styledata', function () {
                layerManager.attachLayers();
            });
        }
        else {
            var _b = this.createStyleInner_(baselayerId), style = _b.style, type = _b.type;
            this.styles_ = style;
            this.baseLayerType_ = type;
        }
        this.currentStyle_ = baselayerId;
        this.updateStyles();
        mbMap.fire(MapEventType.BASELAYER_CHANGE, { baselayerId: baselayerId });
    };
    /**
     * @param {string} language Language
     */
    MapStyler.prototype.setLanguage = function (language) {
        var _this = this;
        var sourceId = 'openmaptiles';
        if (this.styles_.sources[sourceId] && this.styles_.sources[sourceId].url) {
            var sourceUrl = this.styles_.sources[sourceId].url;
            if (!this.langsSourceLayers_) {
                fetch(sourceUrl)
                    .then(function (response) { return response.json(); })
                    .then(function (data) {
                    _this.langsSourceLayers_ = MapStyler.getAvailableLanguagesVectorTiles(data.vector_layers);
                    _this.setLanguageLayers_(sourceId, language);
                });
            }
            else {
                this.setLanguageLayers_(sourceId, language);
            }
        }
    };
    /**
     * @private
     * @param {string} sourceId Source id
     * @param {string} language Language
     */
    MapStyler.prototype.setLanguageLayers_ = function (sourceId, language) {
        var _this = this;
        var mbMap = this.map_.getMapboxglMap();
        // mbMap.read
        if (this.map_.isReady()) {
            this.changeLayersTextFieldLanguage_(sourceId, language, mbMap);
        }
        else {
            mbMap.once('load', function () {
                _this.changeLayersTextFieldLanguage_(sourceId, language, mbMap);
            });
        }
    };
    /**
     * @private
     * @param {string} sourceId Source id
     * @param {string} language Language
     * @param {import("maplibre-gl").Map} mbMAp Language
     */
    MapStyler.prototype.changeLayersTextFieldLanguage_ = function (sourceId, language, mbMap) {
        var _this = this;
        this.styles_.layers.forEach(function (layer) {
            if (layer.source === sourceId) {
                var sourceLayer = layer['source-layer'];
                var availableLanguages = _this.langsSourceLayers_[sourceLayer];
                if (language === null || availableLanguages.indexOf(language) !== -1) {
                    if (layer.type === 'symbol' && layer.layout['text-field']) {
                        mbMap.setLayoutProperty(layer.id, 'text-field', (language ? ['coalesce', ['get', "name:".concat(language)], ['get', 'name']] : layer.layout['text-field']));
                    }
                }
            }
        });
    };
    /**
     * @param {Array.<Object>} vectorLayers Vector layers definition
     * @return {Object.<string, Array.<string>>} Available languages
     */
    MapStyler.getAvailableLanguagesVectorTiles = function (vectorLayers) {
        var langsSourceLayers = {};
        var _loop_1 = function (i) {
            var available = [];
            var vectorLayer = vectorLayers[i];
            Object.keys(vectorLayers[i].fields).forEach(function (elem) {
                if (elem.indexOf('name:') !== -1) {
                    available.push(elem.replace('name:', ''));
                }
                langsSourceLayers[vectorLayer.id] = available;
            });
        };
        for (var i = 0; i < vectorLayers.length; i += 1) {
            _loop_1(i);
        }
        return langsSourceLayers;
    };
    /**
     * @param {Array.<Object>} vectorLayers Vector layers definition
     * @param {string} sourceLayer Source layer
     * @return {Array.<string>} Available languages
     */
    MapStyler.getAvailableLanguagesSourceLayer = function (vectorLayers, sourceLayer) {
        var available = [];
        for (var i = 0; i < vectorLayers.length; i += 1) {
            var vectorLayer = vectorLayers[i];
            if (vectorLayer.id === sourceLayer) {
                Object.keys(vectorLayer.fields).forEach(function (elem) {
                    if (elem.indexOf('name:') !== -1) {
                        available.push(elem.replace('name:', ''));
                    }
                });
            }
        }
        return available;
    };
    MapStyler.prototype.initializenSatelliteLayerEvent = function () {
        var _this = this;
        this.onMoveSatelliteLayer();
        this.map_.on('moveend', function () {
            _this.onMoveSatelliteLayer();
        });
    };
    MapStyler.prototype.onMoveSatelliteLayer = function () {
        if (this.currentStyle_ === MapTypes.SATELLITE) {
            var mapboxglMap = this.map_.getMapboxglMap();
            var satelliteLayer = mapboxglMap.getLayer('satellite-tiles');
            if (!satelliteLayer)
                return;
            var bounds = mapboxglMap.getBounds();
            var zoom = this.map_.getZoom();
            var boundsInsidePolygon = MapStyler.boundsInsidePolygon(bounds, SPAIN_BOUNDS);
            boundsInsidePolygon = boundsInsidePolygon || MapStyler.boundsInsidePolygon(bounds, CANARY_ISLANDS_BOUNDS);
            var satelliteSource = zoom > 6 && boundsInsidePolygon ? 'satellite-pnoa' : 'satellite';
            if (satelliteLayer.source === satelliteSource)
                return;
            if (satelliteSource === 'satellite-pnoa') {
                this.createNewSatelliteLayer('satellite-tiles', 'satellite-pnoa');
            }
            else {
                this.createNewSatelliteLayer('satellite-tiles', 'satellite');
            }
        }
    };
    MapStyler.boundsInsidePolygon = function (bounds, polygon) {
        var insidePolygon = true;
        var idx = 0;
        var boundsCorners = [
            new LonLat(bounds.getWest(), bounds.getNorth()),
            new LonLat(bounds.getEast(), bounds.getNorth()),
            new LonLat(bounds.getEast(), bounds.getSouth()),
            new LonLat(bounds.getWest(), bounds.getSouth()),
        ];
        do {
            insidePolygon = geofencing(polygon, boundsCorners[idx]);
            idx += 1;
        } while (insidePolygon && idx < boundsCorners.length);
        return insidePolygon;
    };
    MapStyler.prototype.createNewSatelliteLayer = function (id, source) {
        var mapboxglMap = this.map_.getMapboxglMap();
        mapboxglMap.removeLayer(id);
        var newSatelliteLayer = {
            id: id,
            type: 'raster',
            'raster-saturation': -1,
            minzoom: 2,
            maxzoom: 22,
        };
        newSatelliteLayer.source = source;
        mapboxglMap.addLayer(newSatelliteLayer, 'water-name-lakeline');
    };
    return MapStyler;
}());
export default MapStyler;
//# sourceMappingURL=MapStyler.js.map